import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Box,
  Slider,
  SliderFilledTrack,
  SliderMark,
  SliderThumb,
  SliderTrack,
  VStack,
  HStack,
  Spacer,
} from '@chakra-ui/react';

import {
  minVal,
  maxVal,
  rightStart,
  leftStart,
  TraitSide,
} from '../Contexts/ConstantsCollection.js';
import { useCharacter } from '../Contexts/CharacterInfo.js';
import { useAction } from '../Contexts/CharacterActions.js';
import RollDisplay from './RollDisplay.jsx';
import TraitButton from './TraitButton.jsx';
import theme from '../../../theme.js';

const CharacterSlider = ({ locked, trait, onLuckPoint }) => {
  const { getDiceIcon, registerRollTrait, unregisterRollTrait } = useAction();
  const { character, updateTraitValue, updateUnsavedChanges } = useCharacter();
  const [tempValue, setTempValue] = useState(
    character.traits[trait.name] ?? 5.5
  );
  const prevValue = useRef(tempValue);
  const [rollNum, setRollNum] = useState(5.5);
  const [isSuccess, setIsSuccess] = useState(false);
  const [showRoll, setShowRoll] = useState(false);
  const [disableButtons, setDisableButtons] = useState(
    character.traits[trait.name] == null ? true : false
  );
  const boxRef = useRef(null);

  const [boxWidth, setBoxWidth] = useState(0);
  const [isLocked, setIsLocked] = useState(locked);
  const [availablePoints, setAvailablePoints] = useState(
    character.availablePoints
  );

  const [trueMaxVal, setTrueMaxVal] = useState(maxVal);
  const [trueMinVal, setTrueMinVal] = useState(minVal);

  const callbackRef = useRef(null);

  const showRollResult = useCallback(
    num => {
      setDisableButtons(true);
      setShowRoll(true);
      if (num === character.traits[trait.name]) onLuckPoint(true);
      setTimeout(() => {
        setDisableButtons(false);
      }, 4000);
      setTimeout(() => {
        setShowRoll(false);
      }, 4000);
    },
    [character.traits, onLuckPoint, trait.name]
  );

  const handleChange = val => {
    prevValue.current = tempValue;
    calculateSliderValue();
    val = val < trueMinVal ? trueMinVal : val > trueMaxVal ? trueMaxVal : val;
    setTempValue(val);
  };

  const calculateSliderValue = availPts => {
    if (typeof availPts == 'undefined') availPts = availablePoints;
    var sliderSet = !isNaN(character.traits[trait.name]);

    var usedPts = !sliderSet
      ? 0
      : character.traits[trait.name] > leftStart
        ? character.traits[trait.name] - leftStart
        : rightStart - character.traits[trait.name];
    var pts = availPts > 0 ? usedPts + availPts : usedPts;

    if (pts >= 4) {
      setTrueMaxVal(maxVal);
      setTrueMinVal(minVal);
    } else if (!sliderSet && availPts <= 0) {
      setIsLocked(true);
      return;
    } else {
      setTrueMaxVal(leftStart + pts);
      setTrueMinVal(rightStart - pts);
    }
    setIsLocked(false);
  };

  const handleChangeEnd = val => {
    const roundedValue =
      val > prevValue.current ? Math.ceil(val) : Math.floor(val);
    setTempValue(roundedValue);
    updateTraitValue(trait.name, roundedValue);
    setDisableButtons(false);
    updateUnsavedChanges(true);
  };

  const handleLeftSideClick = useCallback(
    async num => {
      handleResize();
      setRollNum(num);
      showRollResult(num);
    },
    [showRollResult]
  );

  const handleRigthSideClick = useCallback(
    async num => {
      handleResize();
      setRollNum(num);
      showRollResult(num);
    },
    [showRollResult]
  );

  const handleClick = useCallback(
    num => {
      if (character.traits[trait.name] > 5) handleRigthSideClick(num);
      else handleLeftSideClick(num);
    },
    [character.traits, handleLeftSideClick, handleRigthSideClick, trait.name]
  );

  const handleResize = () => {
    if (boxRef.current) {
      setBoxWidth(boxRef.current.offsetWidth);
    }
  };

  useEffect(() => {
    var points = character.availablePoints - character.usedPoints;
    setAvailablePoints(points);
    calculateSliderValue(points);
    setTempValue(character.traits[trait.name]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [character.availablePoints, character.usedPoints, character.name]);

  useEffect(() => {
    setIsLocked(locked);
    setDisableButtons(character.traits[trait.name] == null ? true : false);
  }, [character.traits, locked, trait.name]);

  useEffect(() => {
    callbackRef.current = (traitName, side, num, success) => {
      if (traitName === trait.name) {
        setIsSuccess(success);
        if (side === TraitSide.Center) handleClick(num);
        else if (side === TraitSide.Left) handleLeftSideClick(num);
        else if (side === TraitSide.Right) handleRigthSideClick(num);
      }
    };
  }, [handleClick, handleLeftSideClick, handleRigthSideClick, trait.name]);

  useEffect(() => {
    const stableFn = (...args) => {
      callbackRef.current?.(...args);
    };
    registerRollTrait(stableFn);
    return () => unregisterRollTrait(stableFn);
  }, [registerRollTrait, unregisterRollTrait]);

  return (
    <VStack width="100%" spacing={0} align="stretch">
      <Box width="auto" align="center">
        <TraitButton
          disabled={disableButtons}
          trait={trait}
          traitSide={TraitSide.Center}
        />
      </Box>
      <Box
        position="relative"
        width="100%"
        zIndex={2}
        display={disableButtons ? 'block' : 'none'}
      >
        <Box
          position="absolute"
          width="100%"
          align="center"
          transform="translateX(0%)"
        >
          {showRoll && (
            <Box
              position="absolute"
              top="-20px"
              left={
                boxWidth * 0.95 * ((rollNum - 1) * 0.1) +
                (boxWidth * 0.075 - 30)
              }
            >
              <RollDisplay
                number={rollNum}
                color={
                  rollNum === character.traits[trait.name]
                    ? theme.colors.luckPointMain
                    : rollNum < character.traits[trait.name]
                      ? theme.colors.rightSideMain
                      : theme.colors.leftSideMain
                }
                success={isSuccess}
                luckPoint={rollNum === character.traits[trait.name]}
              />
            </Box>
          )}
        </Box>
      </Box>
      <Box align="center" width="100%" ref={boxRef}>
        <Slider
          width="85%"
          value={tempValue}
          min={1}
          max={10}
          step={0.1}
          onChange={handleChange}
          onChangeEnd={handleChangeEnd}
          isReadOnly={
            (disableButtons && character.traits[trait.name] % 1 === 0) ||
            isLocked
          }
        >
          {[...Array(10).keys()].map(i => (
            <SliderMark key={i} value={i + 1} mt="3" ml="-1" fontSize="sm">
              {i + 1}
            </SliderMark>
          ))}
          <SliderTrack
            bg={
              character.traits[trait.name] % 1 !== 0
                ? 'gray.300'
                : character.traits[trait.name] <= 5
                  ? 'leftSide.500'
                  : 'leftSide.100'
            }
          >
            <SliderFilledTrack
              bg={
                character.traits[trait.name] % 1 !== 0
                  ? 'gray.300'
                  : character.traits[trait.name] < 6
                    ? 'rightSide.100'
                    : 'rightSide.500'
              }
            />
          </SliderTrack>
          <SliderThumb boxSize={3}>
            <div>
              {getDiceIcon(trait.name, {
                style: {
                  strokeWidth: '1',
                  fontSize: '2rem',
                },
              })}
            </div>
          </SliderThumb>
        </Slider>
      </Box>
      <HStack w="105%" align="center" justify="center" py={4}>
        <TraitButton
          disabled={disableButtons}
          trait={trait.leftSide}
          traitSide={TraitSide.Left}
        />
        <Spacer />
        <TraitButton
          disabled={disableButtons}
          trait={trait.rightSide}
          traitSide={TraitSide.Right}
        />
      </HStack>
    </VStack>
  );
};

export default CharacterSlider;
